import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Users, FileText, CreditCard, ShoppingCart, Package, Truck,
  Calculator, Wallet, MessageCircle, Settings, Search, ChevronDown, LogOut, Menu, Building2, Check, Coins, BellRing, ClipboardList, Repeat, Sparkles, UserCog, BarChart3, Target, Bell, Plus, Shield,
} from "lucide-react";

const NAV_GROUPS = [
  { section: null, items: [{ to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, mod: "core" }] },
  { section: "Intelligence", items: [{ to: "/copilot", label: "NATORIQ Intelligence", icon: Sparkles, mod: "core" }] },
  { section: "Ventes", items: [
    { to: "/clients", label: "Clients", icon: Users, mod: "crm" },
    { to: "/devis", label: "Devis", icon: FileText, mod: "sales" },
    { to: "/factures", label: "Factures", icon: FileText, mod: "sales" },
    { to: "/paiements", label: "Paiements", icon: CreditCard, mod: "sales" },
    { to: "/abonnements", label: "Abonnements", icon: Repeat, mod: "sales" },
    { to: "/relances", label: "Relances", icon: BellRing, mod: "sales" },
  ] },
  { section: "Achats & Stock", items: [
    { to: "/produits", label: "Produits & Stock", icon: Package, mod: "inventory" },
    { to: "/fournisseurs", label: "Fournisseurs", icon: Truck, mod: "purchasing" },
    { to: "/achats", label: "Achats", icon: ClipboardList, mod: "purchasing" },
  ] },
  { section: "Finances", items: [
    { to: "/tresorerie", label: "Trésorerie", icon: Wallet, mod: "treasury" },
    { to: "/depenses", label: "Dépenses", icon: Coins, mod: "treasury" },
    { to: "/comptabilite", label: "Comptabilité", icon: Calculator, mod: "accounting" },
    { to: "/rapports", label: "États financiers", icon: BarChart3, mod: "accounting" },
    { to: "/budgets", label: "Budgets", icon: Target, mod: "accounting" },
  ] },
  { section: "Équipe", items: [
    { to: "/rh", label: "RH & Paie", icon: UserCog, mod: "hr" },
  ] },
  { section: "Canaux", items: [
    { to: "/pos", label: "POS / Caisse", icon: ShoppingCart, mod: "pos" },
    { to: "/module/whatsapp", label: "WhatsApp", icon: MessageCircle, mod: "whatsapp" },
  ] },
  { section: null, items: [{ to: "/module/settings", label: "Paramètres", icon: Settings, mod: "core" }] },
];

function SidebarContent({ onNavigate }) {
  const { activeCompany, user } = useApp();
  const mods = activeCompany?.modules;
  const showItem = (it) => it.mod === "core" || !mods || mods.length === 0 || mods.includes(it.mod);
  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
      isActive ? "bg-primary text-white" : "text-slate-600 hover:bg-secondary hover:text-foreground"
    }`;
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5">
        <div className="font-display text-xl font-extrabold leading-none">NATOR<span className="text-primary">IQ</span></div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1.5">Business Operating System</div>
      </div>
      <nav className="flex-1 px-3 space-y-4 overflow-y-auto pb-6">
        {NAV_GROUPS.map((g, gi) => {
          const items = g.items.filter(showItem);
          if (!items.length) return null;
          return (
            <div key={gi}>
              {g.section && <div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">{g.section}</div>}
              <div className="space-y-1">
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink key={item.to} to={item.to} onClick={onNavigate} className={linkCls} data-testid={`nav-${item.to.split("/").pop()}`}>
                      <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
        {user?.is_super_admin && (
          <div>
            <div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">Administration</div>
            <NavLink to="/admin" onClick={onNavigate} className={linkCls} data-testid="nav-admin">
              <Shield className="w-[18px] h-[18px]" strokeWidth={2} /> Entreprises
            </NavLink>
          </div>
        )}
      </nav>
      <div className="px-5 py-4 border-t border-border text-[11px] text-muted-foreground" data-testid="sidebar-footer">
        NATORIQ · <span className="font-semibold text-foreground">A NATHOR product</span>
      </div>
    </div>
  );
}

function CompanySwitcher() {
  const { companies, activeCompany, switchCompany } = useApp();
  if (!activeCompany) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="rounded-full h-9 gap-2 border-border" data-testid="company-switcher">
          <Building2 className="w-4 h-4 text-primary" />
          <span className="max-w-[140px] truncate font-medium">{activeCompany.trade_name}</span>
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 bg-popover">
        <DropdownMenuLabel>Mes entreprises</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {companies.map((c) => (
          <DropdownMenuItem key={c.id} onClick={() => switchCompany(c.id)} className="cursor-pointer" data-testid={`company-option-${c.id}`}>
            <div className="flex items-center justify-between w-full">
              <div>
                <div className="font-medium">{c.trade_name}</div>
                <div className="text-xs text-muted-foreground">{c.country_code} · {c.base_currency}</div>
              </div>
              {activeCompany.id === c.id && <Check className="w-4 h-4 text-primary" />}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function GlobalSearch() {
  const { activeCompanyId } = useApp();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  const onChange = async (e) => {
    const v = e.target.value;
    setQ(v);
    if (v.length < 2) { setResults([]); setOpen(false); return; }
    try {
      const { data } = await api.get("/search", { params: { q: v, company_id: activeCompanyId } });
      setResults(data.results);
      setOpen(true);
    } catch { setResults([]); }
  };

  return (
    <div className="relative flex-1 max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input value={q} onChange={onChange} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Rechercher client, facture, produit…" className="pl-9 rounded-full h-9 bg-secondary border-transparent" data-testid="global-search-input" />
      {open && results.length > 0 && (
        <div className="absolute top-11 left-0 right-0 bg-popover border border-border rounded-md shadow-lg z-50 overflow-hidden" data-testid="search-results">
          {results.map((r, i) => (
            <div key={i} className="px-3 py-2 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2">
              <span className="text-[10px] uppercase font-semibold text-primary bg-accent px-1.5 py-0.5 rounded">{r.type}</span>
              {r.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CREATE_ITEMS = [
  { label: "Facture", to: "/factures" }, { label: "Devis", to: "/devis" }, { label: "Client", to: "/clients" },
  { label: "Dépense", to: "/depenses" }, { label: "Paiement", to: "/paiements" }, { label: "Produit", to: "/produits" },
  { label: "Employé", to: "/rh" }, { label: "Commande fournisseur", to: "/achats" },
];

function CreateButton() {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="rounded-full h-9 gap-1.5 hidden sm:inline-flex" data-testid="create-button"><Plus className="w-4 h-4" /> Créer</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 bg-popover">
        <DropdownMenuLabel>Créer</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {CREATE_ITEMS.map((it) => (
          <DropdownMenuItem key={it.label} onClick={() => navigate(it.to)} className="cursor-pointer" data-testid={`create-${it.label.toLowerCase().replace(/ /g, "-")}`}>{it.label}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const ACTION_ROUTE = { relances: "/relances", produits: "/produits", depenses: "/depenses", rapports: "/rapports" };

function NotificationBell() {
  const { activeCompanyId } = useApp();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!activeCompanyId) return;
    api.get("/intelligence/attention", { params: { company_id: activeCompanyId } })
      .then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, [activeCompanyId]);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="w-5 h-5" />
          {items.length > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-primary text-white text-[10px] flex items-center justify-center font-semibold">{items.length}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-popover">
        <DropdownMenuLabel>Centre de notifications</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="px-3 py-4 text-sm text-muted-foreground">Tout est à jour 🎉</div>
        ) : items.map((it, i) => (
          <DropdownMenuItem key={i} onClick={() => navigate(ACTION_ROUTE[it.action] || "/dashboard")} className="cursor-pointer flex items-start gap-2 py-2.5" data-testid={`notif-${it.type}`}>
            <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${it.severity === "red" ? "bg-rose-500" : it.severity === "orange" ? "bg-amber-500" : "bg-emerald-500"}`} />
            <span className="text-sm leading-snug">{it.title}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useApp();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-white border-r border-border">
        <SidebarContent />
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 h-16 bg-white border-b border-border flex items-center gap-3 px-4">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button"><Menu className="w-5 h-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 bg-white">
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          <div className="hidden sm:block"><CompanySwitcher /></div>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            <CreateButton />
            <NotificationBell />
            {user?.is_super_admin && (
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} title="Administration" data-testid="admin-link"><Shield className="w-5 h-5 text-primary" /></Button>
            )}
            <span className="hidden md:inline text-sm font-medium text-muted-foreground">FR</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full gap-2 h-9" data-testid="user-menu-button">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm">
                    {user?.full_name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="hidden md:inline max-w-[120px] truncate text-sm">{user?.full_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.full_name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} className="cursor-pointer text-destructive" data-testid="logout-button">
                  <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className="sm:hidden px-4 py-3 border-b border-border bg-white"><CompanySwitcher /></div>

        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
        <footer className="px-4 sm:px-6 lg:px-8 py-4 border-t border-border text-center text-xs text-muted-foreground" data-testid="app-footer">
          NATORIQ · <span className="font-semibold text-foreground">A NATHOR product</span>
        </footer>
      </div>
    </div>
  );
}
